import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pill, Plus, RefreshCw, X, PackagePlus, ClipboardCheck, Scale } from 'lucide-react'
import clsx from 'clsx'
import {
  getInventoryStock, createInventoryProduct, recordInventoryReceipt,
  recordInventoryAdjustment, recordInventoryCount, reconcileInventory,
  getTreatments, getVaccines,
} from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

const today = () => new Date().toISOString().split('T')[0]

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}

const Field = ({ label, children }) => (
  <label className="block mb-3">
    <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
    {children}
  </label>
)
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500'

export default function InventoryList({ ranchId }) {
  const qc = useQueryClient()
  const { canWrite } = useAuth()
  const [modal, setModal] = useState(null)          // 'product' | 'receipt' | 'count' | 'adjust'
  const [activeProduct, setActiveProduct] = useState(null)
  const [banner, setBanner] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['inventoryStock', ranchId],
    queryFn: () => getInventoryStock(ranchId),
  })
  const products = data?.products || []

  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventoryStock', ranchId] })
  const closeModal = () => { setModal(null); setActiveProduct(null) }

  const reconcile = useMutation({
    mutationFn: () => reconcileInventory(ranchId),
    onSuccess: (r) => {
      invalidate()
      setBanner(`Reconciled ${r.products} product(s): ${r.discrepancies} discrepancy(ies), ${r.efficacy_flags} efficacy flag(s).`)
    },
    onError: (e) => setBanner(e?.response?.data?.detail || 'Reconcile failed'),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Pill className="h-6 w-6 text-primary-600" />
            Drug &amp; Vaccine Inventory
          </h1>
          <p className="text-gray-500 mt-1">
            Record purchases and physical counts. The system balances stock bought
            against doses administered — a shortfall is flagged for the audit.
          </p>
        </div>
        {canWrite && (
          <>
            <button onClick={() => reconcile.mutate()} disabled={reconcile.isPending}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={clsx('h-4 w-4', reconcile.isPending && 'animate-spin')} /> Reconcile now
            </button>
            <button onClick={() => setModal('product')}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700">
              <Plus className="h-4 w-4" /> Add product
            </button>
          </>
        )}
      </div>

      {banner && (
        <div className="bg-primary-50 border border-primary-200 text-primary-800 rounded-lg p-3 text-sm flex justify-between">
          <span>{banner}</span>
          <button onClick={() => setBanner(null)}><X className="h-4 w-4" /></button>
        </div>
      )}
      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">{error.message}</div>}

      <div className="card overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading inventory…</p>
        ) : products.length === 0 ? (
          <p className="p-6 text-gray-500">No products yet. Add a drug or vaccine to start tracking stock.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Product', 'Purchased', 'Used', 'Expected on hand', 'Last count', 'Residual', ''].map((h, i) => (
                    <th key={h} className={clsx('px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider',
                      i === 0 ? 'text-left' : i === 6 ? 'text-right' : 'text-right')}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((p) => {
                  const short = p.residual != null && p.residual < -Math.max(2, 0.1 * p.expected_on_hand)
                  return (
                    <tr key={p.product_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{p.name}</div>
                        <div className="text-xs text-gray-400 capitalize">{p.product_type} · {p.unit}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{p.receipts}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{p.consumed}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">{p.expected_on_hand}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">
                        {p.latest_count == null ? '—' : p.latest_count}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        {p.residual == null ? <span className="text-gray-300">—</span>
                          : <span className={clsx('font-medium', short ? 'text-red-600' : Math.abs(p.residual) > 0 ? 'text-amber-600' : 'text-green-600')}>{p.residual}</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canWrite && (
                          <>
                            <button title="Record purchase" onClick={() => { setActiveProduct(p); setModal('receipt') }}
                              className="p-1.5 text-gray-400 hover:text-primary-600"><PackagePlus className="h-4 w-4" /></button>
                            <button title="Record count" onClick={() => { setActiveProduct(p); setModal('count') }}
                              className="p-1.5 text-gray-400 hover:text-primary-600"><ClipboardCheck className="h-4 w-4" /></button>
                            <button title="Adjust (wastage/expiry)" onClick={() => { setActiveProduct(p); setModal('adjust') }}
                              className="p-1.5 text-gray-400 hover:text-primary-600"><Scale className="h-4 w-4" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'product' && <ProductModal ranchId={ranchId} onClose={closeModal} onDone={() => { invalidate(); closeModal() }} setBanner={setBanner} />}
      {modal === 'receipt' && <ReceiptModal ranchId={ranchId} product={activeProduct} onClose={closeModal} onDone={() => { invalidate(); closeModal() }} setBanner={setBanner} />}
      {modal === 'count' && <CountModal ranchId={ranchId} product={activeProduct} onClose={closeModal} onDone={() => { invalidate(); closeModal() }} setBanner={setBanner} />}
      {modal === 'adjust' && <AdjustModal ranchId={ranchId} product={activeProduct} onClose={closeModal} onDone={() => { invalidate(); closeModal() }} setBanner={setBanner} />}
    </div>
  )
}

function useSubmit(fn, onDone, setBanner) {
  return useMutation({
    mutationFn: fn,
    onSuccess: onDone,
    onError: (e) => setBanner(e?.response?.data?.detail || 'Save failed'),
  })
}

function ProductModal({ ranchId, onClose, onDone, setBanner }) {
  const [f, setF] = useState({ name: '', product_type: 'drug', unit: 'ml', manufacturer: '', treatment_reference_id: '', vaccine_reference_id: '' })
  const { data: treatments } = useQuery({ queryKey: ['treatments'], queryFn: () => getTreatments(), enabled: f.product_type === 'drug' })
  const { data: vaccines } = useQuery({ queryKey: ['vaccines'], queryFn: getVaccines, enabled: f.product_type === 'vaccine' })
  const m = useSubmit((body) => createInventoryProduct(ranchId, body), onDone, setBanner)
  const submit = (e) => {
    e.preventDefault()
    const body = { name: f.name, product_type: f.product_type, unit: f.unit, manufacturer: f.manufacturer || null }
    if (f.product_type === 'drug' && f.treatment_reference_id) body.treatment_reference_id = f.treatment_reference_id
    if (f.product_type === 'vaccine' && f.vaccine_reference_id) body.vaccine_reference_id = f.vaccine_reference_id
    m.mutate(body)
  }
  return (
    <Modal title="Add product" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Name *"><input required className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Alamycin LA 100ml" /></Field>
        <Field label="Type"><select className={inputCls} value={f.product_type} onChange={(e) => setF({ ...f, product_type: e.target.value })}>
          <option value="drug">Drug</option><option value="vaccine">Vaccine</option><option value="other">Other</option>
        </select></Field>
        <Field label="Unit *"><input required className={inputCls} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="ml / dose / g" /></Field>
        {f.product_type === 'drug' && (
          <Field label="Link to treatment (so usage is tracked)">
            <select className={inputCls} value={f.treatment_reference_id} onChange={(e) => setF({ ...f, treatment_reference_id: e.target.value })}>
              <option value="">— not linked —</option>
              {treatments?.map(t => <option key={t.id} value={t.id}>{t.treatment_name}{t.drug_name ? ` - ${t.drug_name}` : ''}</option>)}
            </select>
          </Field>
        )}
        {f.product_type === 'vaccine' && (
          <Field label="Link to vaccine (so usage is tracked)">
            <select className={inputCls} value={f.vaccine_reference_id} onChange={(e) => setF({ ...f, vaccine_reference_id: e.target.value })}>
              <option value="">— not linked —</option>
              {vaccines?.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Manufacturer"><input className={inputCls} value={f.manufacturer} onChange={(e) => setF({ ...f, manufacturer: e.target.value })} /></Field>
        <button disabled={m.isPending} className="w-full mt-1 px-3 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50">Save product</button>
      </form>
    </Modal>
  )
}

function ReceiptModal({ ranchId, product, onClose, onDone, setBanner }) {
  const [f, setF] = useState({ quantity: '', unit: product.unit, purchase_date: today(), batch_number: '', supplier_name: '', unit_cost: '', verification_code: '' })
  const m = useSubmit((body) => recordInventoryReceipt(ranchId, body), onDone, setBanner)
  const submit = (e) => {
    e.preventDefault()
    m.mutate({
      product_id: product.product_id, quantity: parseFloat(f.quantity), unit: f.unit,
      purchase_date: f.purchase_date, batch_number: f.batch_number || null,
      supplier_name: f.supplier_name || null, verification_code: f.verification_code || null,
      unit_cost: f.unit_cost ? parseFloat(f.unit_cost) : null,
    })
  }
  return (
    <Modal title={`Record purchase — ${product.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Quantity *"><input required type="number" step="0.01" min="0" className={inputCls} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></Field>
          <Field label="Unit *"><input required className={inputCls} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></Field>
        </div>
        <Field label="Purchase date *"><input required type="date" className={inputCls} value={f.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Batch #"><input className={inputCls} value={f.batch_number} onChange={(e) => setF({ ...f, batch_number: e.target.value })} /></Field>
          <Field label="Unit cost"><input type="number" step="0.01" className={inputCls} value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} /></Field>
        </div>
        <Field label="Supplier"><input className={inputCls} value={f.supplier_name} onChange={(e) => setF({ ...f, supplier_name: e.target.value })} /></Field>
        <Field label="Manufacturer verification code"><input className={inputCls} value={f.verification_code} onChange={(e) => setF({ ...f, verification_code: e.target.value })} placeholder="anti-counterfeit code" /></Field>
        <button disabled={m.isPending} className="w-full mt-1 px-3 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50">Save purchase</button>
      </form>
    </Modal>
  )
}

function CountModal({ ranchId, product, onClose, onDone, setBanner }) {
  const [f, setF] = useState({ counted_quantity: '', unit: product.unit, count_date: today(), batch_number: '', is_audit: false })
  const m = useSubmit((body) => recordInventoryCount(ranchId, body), onDone, setBanner)
  const submit = (e) => {
    e.preventDefault()
    m.mutate({
      product_id: product.product_id, counted_quantity: parseFloat(f.counted_quantity), unit: f.unit,
      count_date: f.count_date, batch_number: f.batch_number || null, is_audit: f.is_audit,
    })
  }
  return (
    <Modal title={`Record count — ${product.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <p className="text-xs text-gray-500 mb-3">Expected on hand right now: <b>{product.expected_on_hand} {product.unit}</b>. Enter what you physically counted.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Counted quantity *"><input required type="number" step="0.01" min="0" className={inputCls} value={f.counted_quantity} onChange={(e) => setF({ ...f, counted_quantity: e.target.value })} /></Field>
          <Field label="Unit *"><input required className={inputCls} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></Field>
        </div>
        <Field label="Count date *"><input required type="date" className={inputCls} value={f.count_date} onChange={(e) => setF({ ...f, count_date: e.target.value })} /></Field>
        <label className="flex items-center gap-2 text-sm text-gray-700 mb-3">
          <input type="checkbox" checked={f.is_audit} onChange={(e) => setF({ ...f, is_audit: e.target.checked })} />
          Independent audit count (counted by an auditor, not self)
        </label>
        <button disabled={m.isPending} className="w-full mt-1 px-3 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50">Save count</button>
      </form>
    </Modal>
  )
}

function AdjustModal({ ranchId, product, onClose, onDone, setBanner }) {
  const [f, setF] = useState({ magnitude: '', direction: 'out', reason: 'wastage', adjustment_date: today(), batch_number: '' })
  const m = useSubmit((body) => recordInventoryAdjustment(ranchId, body), onDone, setBanner)
  const submit = (e) => {
    e.preventDefault()
    const qty = parseFloat(f.magnitude) * (f.direction === 'out' ? -1 : 1)
    m.mutate({ product_id: product.product_id, quantity: qty, reason: f.reason, adjustment_date: f.adjustment_date, batch_number: f.batch_number || null })
  }
  return (
    <Modal title={`Adjust stock — ${product.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Quantity *"><input required type="number" step="0.01" min="0" className={inputCls} value={f.magnitude} onChange={(e) => setF({ ...f, magnitude: e.target.value })} /></Field>
          <Field label="Direction"><select className={inputCls} value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })}>
            <option value="out">Remove (wastage/loss)</option><option value="in">Add (found/correction)</option>
          </select></Field>
        </div>
        <Field label="Reason"><select className={inputCls} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}>
          {['wastage', 'expiry', 'breakage', 'correction', 'transfer_in', 'transfer_out'].map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
        </select></Field>
        <Field label="Date *"><input required type="date" className={inputCls} value={f.adjustment_date} onChange={(e) => setF({ ...f, adjustment_date: e.target.value })} /></Field>
        <Field label="Batch #"><input className={inputCls} value={f.batch_number} onChange={(e) => setF({ ...f, batch_number: e.target.value })} /></Field>
        <button disabled={m.isPending} className="w-full mt-1 px-3 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50">Save adjustment</button>
      </form>
    </Modal>
  )
}
